import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import SimpleLayout from '../components/SimpleLayout';
import MultipleChoiceSurveyTemplates, { templates as multipleChoiceTemplates } from '../components/surveys/creation/surveyFormTemplates/multipleChoiceSurveyTemplates';
import OpenEndedSurveyTemplates, { templates as openEndedTemplates } from '../components/surveys/creation/surveyFormTemplates/openEndedSurveyTemplates';
import { DEFAULT_DESIGN } from '../components/surveys/creation/surveyDefaults';
import SurveyDesignCustomization from '../components/surveys/creation/SurveyDesignCustomization';
import SurveyPreview from '../components/surveys/creation/SurveyPreview';
import adminSurveyService from '../services/adminSurveyService';

const CreateSurveyForm = () => {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [existingSurvey, setExistingSurvey] = useState(null);
  const [surveyType, setSurveyType] = useState(type || 'multiple-choice');
  
  // Template state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [includeOther, setIncludeOther] = useState(true);
  const [afterSurveyAction, setAfterSurveyAction] = useState('reopen');
  const [visibilityRule, setVisibilityRule] = useState('only_unanswered');

  // Design customization state
  const [colorSettings, setColorSettings] = useState(DEFAULT_DESIGN.colors);
  const [typographySettings, setTypographySettings] = useState(DEFAULT_DESIGN.typography);
  const [spacingSettings, setSpacingSettings] = useState(DEFAULT_DESIGN.spacing);
  const [sizeSettings, setSizeSettings] = useState(DEFAULT_DESIGN.size);
  const [textSettings, setTextSettings] = useState(DEFAULT_DESIGN.text);
  const [presentationSettings, setPresentationSettings] = useState(DEFAULT_DESIGN.presentation);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Define validation schema
  const baseSchema = z.object({
    name: z.string().min(1, { message: 'Survey name is required' }),
    displayTrigger: z.enum(['immediately', 'after_delay']),
    delayTime: z.number().optional(),
    question: z.string().min(1, { message: 'Question is required' }),
  });

  const multipleChoiceSchema = baseSchema.extend({
    options: z.array(z.string().min(1, { message: 'Option must not be empty' }))
      .min(2, { message: 'At least 2 options required' }),
    type: z.literal('multiple-choice'),
  });

  const openEndedSchema = baseSchema.extend({
    options: z.optional(z.array(z.string())),
    type: z.literal('open-ended'),
  });

  const surveyFormSchema = z.discriminatedUnion('type', [multipleChoiceSchema, openEndedSchema]);

  const form = useForm({
    resolver: zodResolver(surveyFormSchema),
    defaultValues: {
      name: '',
      displayTrigger: 'immediately',
      delayTime: 0,
      question: '',
      options: surveyType === 'multiple-choice' ? ['', '', ''] : undefined,
      type: surveyType,
    },
    mode: 'onChange', // Enable real-time validation
  });

  // Check if we're editing an existing survey
  useEffect(() => {
    if (id) {
      setIsEditing(true);
      fetchExistingSurvey();
    } else {
      // If creating new survey, set the type from URL params
      setSurveyType(type || 'multiple-choice');
    }
  }, [id, type]);

  const fetchExistingSurvey = async () => {
    try {
      setIsLoading(true);
      const survey = await adminSurveyService.getAdminSurveyById(id);
      if (survey) {
        setExistingSurvey(survey);
        
        // Prepare options for form reset
        const surveyOptions = survey.option && Array.isArray(survey.option) && survey.option.length > 0 
          ? survey.option 
          : (survey.type === 'multiple-choice' ? ['', '', ''] : undefined);
        
        // Set the survey type from the existing survey
        setSurveyType(survey.type || 'multiple-choice');
        
        // Populate form with existing data
        form.reset({
          name: survey.name || '',
          displayTrigger: survey.displayType === 'after_delay' ? 'after_delay' : 'immediately',
          delayTime: survey.display || 0,
          question: survey.question || '',
          options: surveyOptions,
          type: survey.type || 'multiple-choice',
        });
        
        // Set other state immediately
        setIncludeOther(survey.otherOption || false);
        setAfterSurveyAction(survey.closeButtonShow ? 'reopen' : 'nothing');
        setVisibilityRule(survey.visibilityRule || 'all');
        
        // Restore selected template based on question content
        if (survey.question) {
          // Get template data based on survey type
          const templateData = survey.type === 'multiple-choice' 
            ? multipleChoiceTemplates
            : openEndedTemplates;
          
          // Find matching template by comparing question text
          const matchingTemplate = templateData.find(template => 
            template.question === survey.question
          );
          
          if (matchingTemplate) {
            setSelectedTemplate(matchingTemplate);
            setSelectedTemplateId(matchingTemplate.id);
          } else {
            // If no exact match found, try to find by partial match
            const partialMatch = templateData.find(template => 
              survey.question.includes(template.question) || 
              template.question.includes(survey.question)
            );
            
            if (partialMatch) {
              setSelectedTemplate(partialMatch);
              setSelectedTemplateId(partialMatch.id);
            }
          }
        }
        
        // Set design settings
        setColorSettings(survey.colorSettings || DEFAULT_DESIGN.colors);
        setTypographySettings(survey.typographySettings || DEFAULT_DESIGN.typography);
        setSpacingSettings(survey.spacingSettings || DEFAULT_DESIGN.spacing);
        setSizeSettings(survey.sizeSettings || DEFAULT_DESIGN.size);
        setTextSettings(survey.textSettings || DEFAULT_DESIGN.text);
        setPresentationSettings(survey.presentationSettings || DEFAULT_DESIGN.presentation);
        
        // Ensure options are properly set in form state
        if (survey.option && Array.isArray(survey.option) && survey.option.length > 0) {
          // Use setTimeout to ensure form reset is complete
          setTimeout(() => {
            form.setValue('options', survey.option, { shouldValidate: true });
          }, 50);
        }
        
        // Trigger form validation after everything is set
        setTimeout(() => {
          form.trigger();
          setIsLoading(false);
        }, 100);
      }
    } catch (error) {
      console.error('Error fetching existing survey:', error);
      setIsLoading(false);
    }
  };

  const options = form.watch('options') || [];
  const setOptions = (newOptions) => {
    // Ensure we're setting a valid array
    const validOptions = Array.isArray(newOptions) ? newOptions : [];
    form.setValue('options', validOptions, { shouldValidate: true });
  };

      // Watch for options changes and trigger validation
  useEffect(() => {
    if (surveyType === 'multiple-choice' && options.length > 0) {
      form.trigger('options');
    }
  }, [options, surveyType, form]);
  
  // Force re-render of preview when editing and form data is loaded
  useEffect(() => {
    if (isEditing && existingSurvey) {
      // This will trigger a re-render of the preview component
      const timer = setTimeout(() => {
        form.trigger();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isEditing, existingSurvey, form]);
  
  // Additional effect to ensure preview updates when form data changes
  useEffect(() => {
    if (isEditing && existingSurvey && options.length > 0) {
      // Force a re-render when options are loaded
      const timer = setTimeout(() => {
        form.trigger('options');
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isEditing, existingSurvey, options, form]);

  const handleQuestionTextChange = (value) => {
    form.setValue('question', value, { shouldValidate: true });
  };

  const handleOptionChange = (index, value) => {
    const updatedOptions = [...options];
    updatedOptions[index] = value;
    setOptions(updatedOptions);
    
    // Trigger validation for the options field
    setTimeout(() => {
      form.trigger('options');
    }, 100);
  };

  const handleOptionDelete = (index) => {
    const updatedOptions = [...options];
    updatedOptions.splice(index, 1);
    setOptions(updatedOptions);
    
    // Trigger validation for the options field
    setTimeout(() => {
      form.trigger('options');
    }, 100);
  };

  const validateOptions = () => {
    const updatedOptions = options.map(option => option.trim());
    setOptions(updatedOptions);
    return updatedOptions.every(option => option.length > 0);
  };

  const validateQuestion = () => {
    const question = form.watch('question').trim();
    form.setValue('question', question, { shouldValidate: true });
    return question.length > 0;
  };

  const handleIncludeOtherToggle = () => {
    setIncludeOther(!includeOther);
  };

  const handleAddOption = () => {
    setOptions([...options, '']);
    
    // Trigger validation for the options field
    setTimeout(() => {
      form.trigger('options');
    }, 100);
  };

  // handler to fire when a user clicks "Use this template"
  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setSelectedTemplateId(template.id);
    
    // set the form's question
    form.setValue('question', template.question, { shouldValidate: true });

    // if multiple-choice, also set the options
    if (surveyType === 'multiple-choice' && 'options' in template) {
      form.setValue('options', template.options, { shouldValidate: true });
    }
  };

  const isAfterDelay = form.watch('displayTrigger') === 'after_delay';

  // 1. Define your fallback defaults:
  const defaultOptions = [
    'Shipping details & delivery time',
    'Return & exchange policy',
    'Product fit (size, color, etc.)',
    'Customer reviews & ratings'
  ];

  // 2. Check if at least one real (non‐empty) option exists:
  const hasValidOptions = options.some(opt => opt && opt.trim().length > 0);
  
  // Debug log for options
  console.log('Options state:', options);
  console.log('Has valid options:', hasValidOptions);
  console.log('Is editing:', isEditing);
  console.log('Existing survey:', existingSurvey);

  // 3. Build previewQuestions using form state (simplified approach like main project):
  const previewQuestions = [
    {
      id: 1,
      text:
        form.watch('question') ||
        (surveyType === 'multiple-choice'
          ? 'What brings you to our website today?'
          : 'What would you like to know before making a purchase?'),
      options:
        surveyType === 'multiple-choice'
          ? (hasValidOptions ? options.filter(opt => opt && opt.trim().length > 0) : defaultOptions)
          : [],
      includeOther,
      type: surveyType,
    },
  ];

  const onSubmit = async (data) => {
    // For multiple choice surveys, validate options before submission
    if (surveyType === 'multiple-choice') {
      const trimmedOptions = data.options.map(option => option.trim());
      const hasEmptyOptions = trimmedOptions.some(option => option.length === 0);
      const hasEnoughOptions = trimmedOptions.length >= 2;
      
      if (hasEmptyOptions) {
        setError("All options must be filled out.");
        return;
      }
      
      if (!hasEnoughOptions) {
        setError("At least 2 options are required.");
        return;
      }
    }

    const closeButtonShow = afterSurveyAction === 'reopen' ? true : false;

    console.debug('[Admin] Submitting survey with settings:', {
      surveyType,
      includeOther,
      afterSurveyAction,
      visibilityRule,
    });

    const surveyData = {
      type: surveyType,
      name: data.name,
      displayType: data.displayTrigger,
      display: data.displayTrigger === 'after_delay' ? data.delayTime ?? 0 : 0,
      otherOption: includeOther,
      question: data.question,
      product: [], // Add product selection logic here if needed
      option: surveyType === 'multiple-choice' ? data.options || [] : [],
      closeButtonShow,
      visibilityRule,
      colorSettings,
      typographySettings,
      spacingSettings,
      sizeSettings,
      textSettings,
      presentationSettings,
    };

    try {
      setIsSubmitting(true);
      setError('');

      if (isEditing && existingSurvey) {
        await adminSurveyService.updateAdminSurvey(existingSurvey.id, surveyData);
      } else {
        await adminSurveyService.createAdminSurvey(surveyData);
      }
      
      // Navigate back to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error(`Survey submission failed:`, err);
      setError(err?.response?.data?.message || err.message || "Something went wrong while submitting the survey. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTitle = () => {
    if (isEditing) {
      return surveyType === 'multiple-choice' ? 'Edit Multiple Choice Survey' : 'Edit Open-ended Survey';
    }
    return surveyType === 'multiple-choice' ? 'Create Multiple Choice Survey' : 'Create Open-ended Survey';
  };

  const getBackTo = () => {
    // If editing an existing survey, go back to dashboard
    // If creating a new survey, go back to create survey page
    return isEditing ? '/dashboard' : '/dashboard/surveys/create';
  };

  return (
    <SimpleLayout title={getTitle()} backTo={getBackTo()}>
      <div className='' style={{ height: `${window.innerHeight - 110}px` }}>
        <div className="pb-8 pt-0 max-w-7xl mx-auto">
          {isLoading && isEditing ? (
            <div className="flex items-center justify-center py-8">
              <div className="relative">
                <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                <div className="absolute inset-0 w-12 h-12 border-4 border-transparent border-t-blue-400 rounded-full animate-ping"></div>
              </div>
              <div className="ml-4">
                <p className="text-gray-600 font-medium">Loading survey data...</p>
                <p className="text-gray-400 text-sm">Please wait while we load your survey</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-6">
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <div className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-all duration-300">
                  <h3 className="font-medium text-gray-900 mb-4">Survey Name</h3>
                  <div className="h-[60px]">
                    <input
                      type="text"
                      {...form.register('name')}
                      placeholder="Enter survey name"
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-[#2962FF] ${
                        form.formState.errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {form.formState.errors.name && (
                      <p className="text-red-600 text-sm mt-1 ml-1">{form.formState.errors.name.message}</p>
                    )}
                  </div>
                </div>

                <div className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-all duration-300">
                  <h3 className="font-medium text-gray-900 mb-4">Choose when the survey appears to your website visitors</h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="immediately"
                        value="immediately"
                        {...form.register('displayTrigger')}
                        className="h-4 w-4 text-[#2962FF] focus:ring-[#2962FF] border-gray-300"
                      />
                      <label htmlFor="immediately" className="font-normal">
                        Immediately
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="after_delay"
                        value="after_delay"
                        {...form.register('displayTrigger')}
                        className="h-4 w-4 text-[#2962FF] focus:ring-[#2962FF] border-gray-300"
                      />
                      <label htmlFor="after_delay" className="font-normal">
                        After Time Delay
                      </label>
                    </div>
                  </div>

                  {isAfterDelay && (
                    <div className="mt-4 flex items-center">
                      <input
                        type="number"
                        {...form.register('delayTime', { valueAsNumber: true })}
                        className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#2962FF]"
                        min={1}
                      />
                      <span className="ml-3">Seconds</span>
                    </div>
                  )}
                </div>

                {/* "After Survey is Closed" section */}
                <div className="rounded-lg border bg-white shadow-sm">
                  <div className="flex flex-col space-y-1.5 p-6 pb-3">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900">After Survey is Closed</h3>
                      <div className="relative group">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 hidden group-hover:block bg-gray-800 text-white text-xs rounded p-2 max-w-xs z-10">
                          <p className='w-[167px]'>Choose what happens after a visitor closes the survey popup.</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500">Choose what happens after a visitor closes the survey popup.</p>
                  </div>
                  <div className="p-6 pt-0">
                    <div className="space-y-4">
                      <label
                        className="flex items-center space-x-3 rounded-lg border border-transparent p-3 text-sm transition-colors hover:bg-gray-100"
                        htmlFor="nothing"
                      >
                        <input
                          type="radio"
                          value="nothing"
                          id="nothing"
                          name="afterSurveyAction"
                          checked={afterSurveyAction === "nothing"}
                          onChange={() => setAfterSurveyAction("nothing")}
                          className="w-4 h-4"
                        />
                        <div>
                          <p className="text-sm">Do nothing – don't show anything afterwards</p>
                        </div>
                      </label>

                      <label
                        className={`flex items-center space-x-3 rounded-lg p-3 text-sm transition-all relative ${afterSurveyAction === "reopen"
                          ? "border-2 border-[#2962FF]/40 bg-[#2962FF]/5"
                          : "border border-gray-200 hover:border-[#2962FF]/20 hover:bg-[#2962FF]/5"
                          }`}
                        htmlFor="reopen"
                      >
                        <input
                          type="radio"
                          value="reopen"
                          id="reopen"
                          name="afterSurveyAction"
                          checked={afterSurveyAction === "reopen"}
                          onChange={() => setAfterSurveyAction("reopen")}
                          className="w-4 h-4 text-[#2962FF]"
                        />
                        <div className="space-y-1.5">
                          <div className="flex items-baseline gap-2">
                            <p className="text-sm">Show a "What was missing?" button to reopen the survey</p>
                            <span className="bg-[#2962FF] hover:bg-[#2962FF]/90 text-white px-2 py-0.5 text-xs font-medium rounded animate-pulse">
                              Recommended
                            </span>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* "After Survey answer is Submitted" section */}
                <div className="rounded-lg border bg-white shadow-sm">
                  <div className="flex flex-col space-y-1.5 p-6 pb-3">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900">After the user submits their answer to the Admin Survey.</h3>
                      <div className="relative group">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 hidden group-hover:block bg-gray-800 text-white text-xs rounded p-2 max-w-xs z-10">
                          <p className='w-[167px]'>Choose the users who will see the admin survey.</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500">Choose the users who will see the admin survey.</p>
                  </div>
                  <div className="p-6 pt-0">
                    <div className="space-y-4">
                      <label
                        className="flex items-center space-x-3 rounded-lg border border-transparent p-3 text-sm transition-colors hover:bg-gray-100"
                        htmlFor="visibility_all"
                      >
                        <input
                          type="radio"
                          value="all"
                          id="visibility_all"
                          name="visibilityRule"
                          checked={visibilityRule === "all"}
                          onChange={() => setVisibilityRule("all")}
                          className="w-4 h-4"
                        />
                        <div>
                          <p className="text-sm">Display to all users</p>
                        </div>
                      </label>

                      <label
                        className={`flex items-center space-x-3 rounded-lg p-3 text-sm transition-all relative ${visibilityRule === "only_unanswered"
                          ? "border-2 border-[#2962FF]/40 bg-[#2962FF]/5"
                          : "border border-gray-200 hover:border-[#2962FF]/20 hover:bg-[#2962FF]/5"
                          }`}
                        htmlFor="visibility_only_unanswered"
                      >
                        <input
                          type="radio"
                          value="only_unanswered"
                          id="visibility_only_unanswered"
                          name="visibilityRule"
                          checked={visibilityRule === "only_unanswered"}
                          onChange={() => setVisibilityRule("only_unanswered")}
                          className="w-4 h-4 text-[#2962FF]"
                        />
                        <div className="space-y-1.5">
                          <div className="flex items-baseline gap-2">
                            <p className="text-sm">Show only to users who have not submitted any survey answers.</p>
                            <span className="bg-[#2962FF] hover:bg-[#2962FF]/90 text-white px-2 py-0.5 text-xs font-medium rounded animate-pulse">
                              Recommended
                            </span>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  {surveyType === 'multiple-choice' ? (
                    <MultipleChoiceSurveyTemplates
                      selectedTemplateId={selectedTemplateId}
                      onSelectTemplate={handleSelectTemplate}
                    />
                  ) : (
                    <OpenEndedSurveyTemplates
                      selectedTemplateId={selectedTemplateId}
                      onSelectTemplate={handleSelectTemplate}
                    />
                  )}
                </div>

                <div className="p-6 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-all duration-300">
                  <h3 className="font-medium text-gray-900 mb-4">Survey Questions</h3>
                  {surveyType !== 'multiple-choice' && (
                    <div>
                      <input
                        type="text"
                        {...form.register('question')}
                        placeholder={selectedTemplate?.question ?? 'Enter your question here...'}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-[#2962FF] ${
                          form.formState.errors.question ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {form.formState.errors.question && (
                        <p className="text-red-600 text-sm mt-1 ml-1">{form.formState.errors.question.message}</p>
                      )}
                    </div>
                  )}

                  {surveyType === 'multiple-choice' && (
                    <div className="mt-2 space-y-4">
                      <div className="bg-[#2962FF]/10 rounded-md p-4 pb-2 border-l-4 border-[#2962FF]">
                        <div className="flex items-center gap-2 mb-2 text-[#2962FF]">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <label className="font-medium text-sm">Survey Question (shown to visitors):</label>
                        </div>
                        <input
                          type="text"
                          {...form.register('question')}
                          placeholder={selectedTemplate?.question ?? 'Enter your question'}
                          className={`w-full h-10 px-3 py-2 border border-[#2962FF]/30 focus:border-[#2962FF] bg-white rounded-md text-sm ${
                            form.formState.errors.question ? 'border-red-500' : ''
                          }`}
                        />
                        {form.formState.errors.question && (
                          <p className="text-red-600 text-sm mt-1 ml-1">{form.formState.errors.question.message}</p>
                        )}
                      </div>
                      <div className="bg-[#F7F8FA] rounded-md p-4 border-l-4 border-[#FFC947]">
                        <div className="flex items-center gap-2 mb-3 text-gray-700">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          <label className="font-medium text-sm">Answer Options (visitors choose one):</label>
                        </div>

                        <div className="space-y-3">
                          {options.map((option, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <input
                                type="text"
                                value={option}
                                onChange={(e) => handleOptionChange(index, e.target.value)}
                                placeholder={`Option ${index + 1}`}
                                className={`flex-1 h-10 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#2962FF] ${
                                  form.formState.errors.options && option.trim() === '' ? 'border-red-500' : 'border-gray-300'
                                }`}
                              />
                              {options.length > 2 && (
                                <button
                                  onClick={() => handleOptionDelete(index)}
                                  className="text-red-500 hover:text-red-700"
                                  aria-label="Delete option"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          ))}
                          {form.formState.errors.options && (
                            <p className="text-red-600 text-sm ml-1">{form.formState.errors.options.message}</p>
                          )}
                          {form.formState.isSubmitted && surveyType === 'multiple-choice' && (
                            <>
                              {options.length < 2 && (
                                <p className="text-red-600 text-sm ml-1">At least 2 options are required.</p>
                              )}
                              {options.some(option => option.trim() === '') && (
                                <p className="text-red-600 text-sm ml-1">All options must be filled out.</p>
                              )}
                            </>
                          )}
                          <button
                            onClick={handleAddOption}
                            className="flex items-center gap-2 mt-2 h-9 px-3 py-2 rounded-md bg-[#FF9E80]/10 text-[#FF9E80] hover:bg-[#FF9E80]/20 hover:text-[#FF9E80] border border-[#FF9E80]/30 text-sm"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Add Answer
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 pt-2">
                        <input
                          type="checkbox"
                          id="include-other"
                          checked={includeOther}
                          onChange={handleIncludeOtherToggle}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                        />
                        <label htmlFor="include-other" className="text-sm">
                          Include "Other" option
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                {/* SurveyDesignCustomization component */}
                <SurveyDesignCustomization
                  colorSettings={colorSettings}
                  typographySettings={typographySettings}
                  spacingSettings={spacingSettings}
                  sizeSettings={sizeSettings}
                  textSettings={textSettings}
                  presentationSettings={presentationSettings}
                  setColorSettings={setColorSettings}
                  setTypographySettings={setTypographySettings}
                  setSpacingSettings={setSpacingSettings}
                  setSizeSettings={setSizeSettings}
                  setTextSettings={setTextSettings}
                  setPresentationSettings={setPresentationSettings}
                  isOpenEnded={type === 'open-ended'}
                />

                {/* Error Display */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700">{error}</p>
                  </div>
                )}

                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                    disabled={isSubmitting}
                  >
                    Discard
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-[#2962FF] text-white rounded-md hover:bg-[#2962FF]/90 transition-colors disabled:opacity-50"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Survey' : 'Create Survey')}
                  </button>
                </div>
              </form>
            </div>

            {/* SurveyPreview */}
            <div className="lg:col-span-6">
              <div className="bg-white p-6 rounded-lg border border-gray-200 sticky top-8 lg:top-0 hover:shadow-md transition-all duration-300">
                <SurveyPreview
                  surveyName={form.watch('name')}
                  questions={previewQuestions}
                  afterSurveyAction={afterSurveyAction}
                  customStyles={{
                    colors: colorSettings,
                    typography: typographySettings,
                    spacing: spacingSettings,
                    size: sizeSettings,
                  }}
                  customText={{
                    submitButtonText: textSettings.submitButtonText,
                    assistantTitle: textSettings.assistantTitle,
                    inputPlaceholder: textSettings.inputPlaceholder,
                    chatButtonText: textSettings.chatButtonText,
                  }}
                  presentationStyle={presentationSettings}
                />
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </SimpleLayout>
  );
};

export default CreateSurveyForm;
